import React, { useEffect, useRef, useState } from "react";
import { withRouter, Prompt } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import { compose } from "redux";
import { withTranslation } from "react-i18next";
import { createStructuredSelector } from "reselect";
import { Typography, Space, Button, Row, Col, Tooltip } from "antd";
import styled from "styled-components";
import ArrowBack from "../../assets/svg/ArrowBack";

import "../Patient/PatientPotentialSavings/PatientPotentialSavings.css";
import { WHITE, BLACK, GRAY_300, PRIMARY_600, PRIMARY_800 } from "../../constant/colors";
import {
  selectFilterList,
  selectUser,
  selectFilters,
  selectIsFetching,
  selectIsFilterListFiltersModified,
  getFilterListSelectedColumns,
  selectTableColumnsModified,
  selectTableLayoutModified,
  selectFilterListStatuses,
  selectIsBulkActionDirty
} from "../../store/selector";
import { fontWeights, sizes } from "../../constant/styles";
import { ReactComponent as DeleteIcon } from "../../assets/svg/delete-new-icon.svg";
import { ReactComponent as InfoSavedList } from "../../assets/svg/info-saved-list.svg";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ErrorMessage from "../../component/customComponent/customMessages/ErrorMessage";
import SaveAsNewList from "../SaveAsNewList";
import { SavedListActionsMenu } from "../SavedList/SavedListActionsMenu";
import { ModalComponent } from "../ModalComponent";
import DiscardListChangesModal from "./DiscardListChangesModal";
import { setPendingBulkLeaveTeardown } from "../BulkActions/pendingBulkLeaveTeardown";
import { defaultFilters } from "../../constant/filters";
import { selectedViewTypes, selectedViews } from "../../constant/table";
import ACTIONS from "../../store/action";
import {
  ApiNames,
  deleteSavedListAPI,
  starFilterList,
  getNumberOfPatientsAPI,
  parseSearchFormParams
} from "../../api/api";
import { Routes } from "../../constant/routes";
import { getTypeIcon, SAVED_LIST_SOURCES } from "../../constant/savedList";
import { StarFilterListColumn, ViewersTooltip } from "../TableSection/CommonColumns";
import { ReactComponent as UserIcon } from "../../assets/svg/user-icon.svg";
import { dateTimeFormatter } from "../../utils/date";
import { getColumnLayoutStorageKey, readColumnLayoutFromSession } from "../../utils/columnLayout";

const { Text } = Typography;

const StyledSaveButton = styled(Button)`
  font-size: ${sizes.medium};
  font-weight: ${fontWeights.semibold};
  border: 1px solid ${PRIMARY_600};
  border-radius: 6px;
  background-color: ${PRIMARY_600};
  height: 31px;
  padding: 4px 16px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${PRIMARY_800};
    border-color: ${PRIMARY_800};
  }

  &:active {
    background: ${PRIMARY_800};
    color: ${WHITE};
    border-color: ${PRIMARY_800};
  }

  &:focus {
    background: ${PRIMARY_600};
    color: ${(props) => (props.isOwner ? PRIMARY_600 : WHITE)};
    border-color: ${PRIMARY_600};
    box-shadow: 0 0 0 4px rgba(83, 177, 253, 0.3) !important;
  }

  &:disabled {
    background-color: ${GRAY_300};
    border-color: ${GRAY_300};
    color: ${WHITE};
    &:hover {
      background-color: ${GRAY_300};
      border-color: ${GRAY_300};
      color: ${WHITE};
    }
  }
`;

const BackButtonStyle = styled(Button)`
  background-color: transparent;
  border: none;
  padding: 0px;
  height: 20px;
`;

const FilterListControlsComponent = ({
  t,
  history,
  filterList,
  user,
  filters,
  updateFilterListAct,
  isFetching,
  setFiltersAct,
  clearSavedListRelatedDataAct,
  setSelectedView,
  clearFiltersInputs,
  setSelectedViewAct,
  filterListFiltersModified,
  markSavedFilterAsChanged,
  filterListSelectedColumns,
  tableColumnsModified,
  tableLayoutModified,
  clearSorterAct,
  searchSavedListByIdAct,
  filterListId
}) => {
  const dispatch = useDispatch();

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDiscardModal, setShowDiscardModal] = useState(false);
  const [fromSavedBackButton, setFromSavedBackButton] = useState(false);
  const [currentStarred, setCurrentStarred] = useState(filterList?.starred);
  const [savedListRecordCount, setSavedListRecordCount] = useState(undefined);
  const cleanupHandledRef = useRef(false);
  const pendingLocationRef = useRef(null);

  const filterListStatuses = useSelector(selectFilterListStatuses);
  const isBulkActionDirty = useSelector(selectIsBulkActionDirty);
  const typeIconData = getTypeIcon(filterList?.type);

  const isOwner = user?.id === filterList?.user;
  const isModified = tableColumnsModified || filterListFiltersModified || tableLayoutModified;
  const canModify = isOwner && isModified;
  // Whenever a bulk action is dirty (with or without filter edits), the global
  // BulkActionNavGuard owns the leave first (its "Discard bulk action?" popup). This
  // component's <Prompt> and Back button defer to it, registering resetSavedFilterListState
  // as the teardown that runs only on the guard's terminal Discard & Leave. When the bulk
  // panel is clean, this component's own discard modal handles unsaved filter edits.

  const updateFilterListStatusesAct = (filterListID, canModify, showDiscardModal) =>
    dispatch(ACTIONS.updateFilterListStatuses(filterListID, canModify, showDiscardModal));
  const clearSelectedViewAct = () => dispatch(ACTIONS.clearSelectedView());

  useEffect(() => {
    updateFilterListStatusesAct(filterList?.id, canModify, false);
  }, [filterList?.id, canModify]);

  useEffect(() => {
    setShowDiscardModal(filterListStatuses.showDiscardModal);
  }, [filterListStatuses.showDiscardModal]);

  useEffect(() => {
    dispatch(ACTIONS.setFilterListDiscardModalVisible(showDiscardModal));
  }, [showDiscardModal]);

  useEffect(
    () => () => {
      dispatch(ACTIONS.setFilterListDiscardModalVisible(false));
    },
    [dispatch]
  );

  useEffect(() => {
    setCurrentStarred(filterList?.starred);
  }, [filterList?.starred]);

  // Fetch record count based on saved list's filters (not current modified filters)
  useEffect(() => {
    const fetchSavedListCount = async () => {
      if (!filterList?.id || !filterList?.selectedView) {
        setSavedListRecordCount(undefined);
        return;
      }

      try {
        const listSelectedView =
          filterList.selectedView === selectedViews.highPriority
            ? selectedViews.topOpportunity
            : filterList.selectedView;
        const params = filterList.params;
        const formData = parseSearchFormParams({
          selectedView: listSelectedView,
          page: 1,
          limit: 1,
          filters: params?.filters || {}
        });

        const { data } = await getNumberOfPatientsAPI(formData?.filters, 0, 1, listSelectedView);
        setSavedListRecordCount(data?.totalPatients ?? null);
      } catch (error) {
        setSavedListRecordCount(null);
      }
    };

    fetchSavedListCount();
  }, [filterList?.id]);

  const handleStarClick = async () => {
    try {
      const newStarredValue = !currentStarred;
      await starFilterList(filterList?.id, newStarredValue);
      setCurrentStarred(newStarredValue);

      if (searchSavedListByIdAct && filterListId) {
        await searchSavedListByIdAct(filterListId);
      }
    } catch (error) {
      ErrorMessage(t("errorMessages.filterlist_starred"));
      setCurrentStarred(!currentStarred);
    }
  };

  const handleSaveButton = async (e) => {
    e.currentTarget.blur();
    updateFilterListStatusesAct(filterList?.id, canModify, false);

    const { columnWidths, columnOrder } = readColumnLayoutFromSession(
      getColumnLayoutStorageKey(filterList?.selectedView, filterList?.id)
    );

    const newFilterListValues = {
      name: filterList?.name,
      description: filterList?.description,
      type: filterList?.type,
      selectedView: filterList?.selectedView,
      params: { filters, selectedColumns: filterListSelectedColumns, columnWidths, columnOrder }
    };

    markSavedFilterAsChanged(filterList?.id, false);
    updateFilterListAct(newFilterListValues, filterList?.id);
  };

  const resetSavedFilterListState = ({ preserveFilters = false } = {}) => {
    if (!preserveFilters) {
      setFiltersAct(defaultFilters);
      clearFiltersInputs();
    }
    clearSavedListRelatedDataAct();
    clearSorterAct(filterList.selectedView);
    // Reset the view's shared table page so the saved-list page doesn't leak into the global
    // bucket view of the same type when leaving the list (sidebar, discard-confirm, back button).
    dispatch(ACTIONS.clearTablesData(filterList.selectedView));
    // Leaving the list also abandons any open bulk selection on it. Clear it here
    // (the single chokepoint for every leave path) so it isn't orphaned — a stray
    // selection in another view would otherwise survive and could re-arm the
    // bulk-action guards on the next view.
    dispatch(ACTIONS.clearAllBulkSelections());
    markSavedFilterAsChanged(undefined, false);
  };

  const handleBrowserBack = (fromSavedListBack = false) => {
    resetSavedFilterListState({ preserveFilters: fromSavedListBack });
    cleanupHandledRef.current = true;

    if (filterListStatuses.showDiscardModal) {
      updateFilterListStatusesAct(filterList?.id, false, false);
      pendingLocationRef.current = null;
      if (filterListStatuses.nextSelectedView === Routes.ROOT) {
        clearSelectedViewAct();
        history.push(Routes.ROOT);
        return;
      }
      setSelectedView(filterListStatuses.nextSelectedView);
      history.push(`/?selectedView=${filterListStatuses.nextSelectedView}`);
      return;
    }

    const pendingLocation = pendingLocationRef.current;
    pendingLocationRef.current = null;
    if (pendingLocation) {
      history.push(pendingLocation);
      return;
    }

    if (history.location.state?.from === selectedViews.savedList || fromSavedListBack) {
      setSelectedView(selectedViews.savedList);
      history.push(`/?selectedView=${selectedViews.savedList}`);
    } else {
      setSelectedView(filterList.selectedView);
      history.push(`/?selectedView=${filterList.selectedView}`);
    }
  };

  return (
    <div style={{ backgroundColor: WHITE }}>
      {/* Stand this <Prompt> down whenever a bulk action is dirty — the global
          BulkActionNavGuard then owns the leave (its "Discard bulk action?" popup fires
          first, and on a canModify list chains into this component's discard modal). Only
          ONE history.block may be active at a time, so ownership is keyed purely on
          isBulkActionDirty: this <Prompt> owns the leave only when the bulk panel is clean. */}
      <Prompt
        when={!isBulkActionDirty}
        message={(location) => {
          if (cleanupHandledRef.current) {
            cleanupHandledRef.current = false;
            return true;
          }
          // Patient detail is a round-trip — preserve the saved list's page/sorter/id so it
          // is restored on return. Other destinations get the normal cleanup.
          if (location.pathname.startsWith(`/${Routes.PATIENT}`)) {
            return true;
          }
          // Unsaved filter/column edits: show the saved-list "Unsaved Changes" modal so the
          // user can Save or knowingly discard them, and block the push until they decide.
          // This fires even when a bulk action was just discarded (both dirty at once) — the
          // two prompts chain, each owning its own dirty state, so filter edits are never
          // dropped without warning. It also fires for "View List" on "Save as New List":
          // that saved the edited state into a NEW list, but the ORIGINAL list being left
          // still has those unsaved changes, so leaving it must still warn.
          if (canModify) {
            pendingLocationRef.current = location;
            setShowDiscardModal(true);
            return false;
          }
          resetSavedFilterListState();
          return true;
        }}
      />
      <Row>
        <Col span={16}>
          <Space direction="vertical" size={0}>
            <Space direction="horizontal">
              <Tooltip title={t("back")}>
                <BackButtonStyle
                  onClick={() => {
                    setFromSavedBackButton(true);
                    if (isBulkActionDirty) {
                      // Bulk-dirty (with or without filter edits): the global BulkActionNavGuard
                      // owns the leave first — its "Discard bulk action?" popup fires, and on a
                      // canModify list it then chains into this component's discard modal. Register
                      // the saved-list teardown to run ONLY on the guard's terminal Discard & Leave
                      // (bulk-only case); attempt the back navigation, which the guard blocks until
                      // the user decides. No eager teardown, so "Stay" leaves the list fully intact.
                      setPendingBulkLeaveTeardown(() => resetSavedFilterListState({ preserveFilters: true }));
                      history.push(`/?selectedView=${selectedViews.savedList}`);
                    } else if (canModify) {
                      setShowDiscardModal(true);
                    } else {
                      handleBrowserBack(true);
                    }
                  }}
                  type="text"
                >
                  <ArrowBack />
                </BackButtonStyle>
              </Tooltip>
            </Space>
            <Space direction="vertical" size={0}>
              <Space>
                {filterList?.id && (
                  <StarFilterListColumn
                    page={1}
                    record={{ ...filterList, starred: currentStarred }}
                    customStarClick={handleStarClick}
                  />
                )}

                <Tooltip
                  title={filterList?.name}
                  color={WHITE}
                  overlayInnerStyle={{
                    color: BLACK,
                    fontSize: sizes.medium,
                    fontWeight: fontWeights.regular
                  }}
                >
                  <div
                    style={{
                      maxWidth: "580px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                  >
                    <Text
                      style={{
                        fontSize: sizes.large,
                        color: BLACK,
                        fontWeight: fontWeights.medium
                      }}
                    >
                      {filterList?.name}
                    </Text>
                  </div>
                </Tooltip>
                {filterList?.description && (
                  <Tooltip
                    color={WHITE}
                    title={filterList?.description}
                    overlayStyle={{
                      maxWidth: "550px"
                    }}
                    overlayInnerStyle={{
                      color: BLACK,
                      fontSize: sizes.medium,
                      fontWeight: fontWeights.regular
                    }}
                  >
                    <InfoSavedList
                      id="list_screen_details"
                      style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
                    />
                  </Tooltip>
                )}
                {typeIconData && (
                  <Tooltip
                    overlayInnerStyle={{
                      backgroundColor: WHITE,
                      color: BLACK,
                      borderRadius: 8,
                      padding: "10px 15px 0 15px"
                    }}
                    overlayClassName="custom-white-tooltip"
                    title={
                      <Space direction="vertical">
                        <Text style={{ color: BLACK, fontWeight: fontWeights.semibold }}>{typeIconData.subtitle}</Text>
                      </Space>
                    }
                  >
                    <div style={{ display: "flex", alignItems: "center" }} id={typeIconData.id}>
                      {typeIconData.icon}
                    </div>
                  </Tooltip>
                )}

                {filterList?.viewers?.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center" }} id="shared_lists_viewers_tag">
                    <ViewersTooltip viewers={filterList?.viewers} owner={filterList?.user} t={t} />
                  </div>
                )}
              </Space>
              <Space>
                <Text style={{ fontSize: sizes.small, color: BLACK, fontWeight: fontWeights.regular }}>
                  {t("savedList.description", {
                    view: selectedViewTypes[filterList?.selectedView]?.description
                  })}
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <UserIcon />
                  <Text style={{ fontSize: sizes.small, color: BLACK, fontWeight: fontWeights.regular }}>
                    {t("savedList.created_by")} {filterList.username}
                  </Text>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                  <Text style={{ fontSize: sizes.small, color: BLACK, fontWeight: fontWeights.regular }}>
                    {t("filter_list_view.filter_list_top_section.last_modified_lbl")}{" "}
                    {dateTimeFormatter(filterList?.updatedAt)}
                  </Text>
                </div>
              </Space>
            </Space>
          </Space>
        </Col>
        <Col
          span={8}
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "end",
            marginTop: 28
          }}
        >
          <Space direction="vertical" align="end" size={6}>
            <Space direction="horizontal" size={16}>
              {isOwner && (
                <StyledSaveButton
                  id="list_screen_save"
                  type="primary"
                  disabled={!canModify}
                  loading={isFetching}
                  onClick={handleSaveButton}
                >
                  {t("filter_list_view.filter_list_top_section.save")}
                </StyledSaveButton>
              )}
              {user?.id && filterList?.user && (
                <SaveAsNewList disabled={false} id="Save_New_Saved_List" isOwner={isOwner} />
              )}
              <SavedListActionsMenu
                record={filterList}
                recordCount={savedListRecordCount}
                deleteAction={() => {
                  setShowDeleteModal(true);
                }}
                source={SAVED_LIST_SOURCES.LISTS_SCREEN}
              />
            </Space>
            <Space>
              {isOwner && canModify && (
                <Text
                  style={{
                    fontSize: sizes.small,
                    color: BLACK,
                    fontWeight: fontWeights.regular,
                    marginRight: 25
                  }}
                >
                  {t("filter_list_view.filter_list_top_section.unsaved_changes")}
                </Text>
              )}
            </Space>
          </Space>
        </Col>
        {filterList && filterList?.name && (
          <DiscardListChangesModal
            open={showDiscardModal}
            onCancel={() => {
              pendingLocationRef.current = null;
              setShowDiscardModal(false);
              setFromSavedBackButton(false);
              // Keep Redux discard state in sync with the local close: resets
              // filterListStatuses.showDiscardModal to false and nextSelectedView to ""
              // (4th arg defaults). This keeps handleBrowserBack routing a later confirm
              // by pendingLocation instead of a stale sidebar target, and lets the modal
              // reopen on the next sidebar click.
              updateFilterListStatusesAct(filterList?.id, canModify, false);
            }}
            onOk={() => {
              handleBrowserBack(fromSavedBackButton);
              setShowDiscardModal(false);
              setFromSavedBackButton(false);
            }}
          />
        )}

        <ModalComponent
          open={showDeleteModal}
          onCancel={() => setShowDeleteModal(false)}
          okText={t("savedList.confirm_modal.ok_text")}
          onOk={async () => {
            try {
              const deleteListRes = await deleteSavedListAPI(filterList.id);
              if (deleteListRes) {
                // Clear all modification flags and filter list data
                resetSavedFilterListState();

                setSelectedViewAct(selectedViews.savedList);
                cleanupHandledRef.current = true;
                history.push({ pathname: "/", search: `?selectedView=` + selectedViews.savedList });
                SuccessMessage(t("savedList.confirm_modal.success_message"));
              }
            } catch (error) {
              ErrorMessage(t("savedList.confirm_modal.error_message"));
            }
          }}
        >
          <Space direction="vertical">
            <DeleteIcon style={{ width: "48px" }} />
            <Text className="title">{t("savedList.confirm_modal.title")}</Text>
            <Text className="content">
              {t("savedList.confirm_modal.content", {
                listName: filterList.name
              })}
            </Text>
          </Space>
        </ModalComponent>
      </Row>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  filterList: selectFilterList,
  user: selectUser,
  filters: selectFilters,
  isFetching: selectIsFetching([ApiNames.UpdateFilterList]),
  filterListFiltersModified: selectIsFilterListFiltersModified,
  filterListSelectedColumns: getFilterListSelectedColumns,
  tableColumnsModified: selectTableColumnsModified,
  tableLayoutModified: selectTableLayoutModified
});

const mapDispatchToProps = (dispatch) => ({
  updateFilterListAct: (newFilterListData, filterListId) =>
    dispatch(ACTIONS.updateFilterListAction(newFilterListData, filterListId)),
  setFiltersAct: (filters, tableName) => dispatch(ACTIONS.setFiltersAction(filters, tableName)),
  setSelectedViewAct: (selectedView) => dispatch(ACTIONS.setSelectedView(selectedView)),
  clearFiltersInputs: () => dispatch(ACTIONS.clearFiltersInputs()),
  clearSorterAct: (view) => dispatch(ACTIONS.clearSorterAction(view)),
  clearSavedListRelatedDataAct: () => dispatch(ACTIONS.clearSavedListRelatedDataAction()),
  setSelectedView: (selectedView) => dispatch(ACTIONS.setSelectedView(selectedView)),
  markSavedFilterAsChanged: (filterListID, isModified) =>
    dispatch(ACTIONS.markSavedFilterAsChanged(filterListID, isModified))
});

const FilterListControls = compose(
  withRouter,
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(FilterListControlsComponent);

export { FilterListControls };
